import React, { useCallback, useEffect, useState } from "react";
import API from "../services/api";
import { Button, Card, Input, Modal, Space, Spin, Switch, Table, Tag, Tooltip, Typography } from "antd";
import RowActions from "../components/RowActions";
import {
  ClearOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import CustomForm from "../components/CustomForm";
import { useSnackbar } from "notistack";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

// Renders a plan's aggregate seat usage: the seats its customers hold between
// them, against what the plan grants them in total.
//
// Aggregate because the allowance is the plan's, so every customer on it shares
// one number -- "25 / 400" is two customers on a 200-seat plan using 25 seats.
//
// The counts come from each customer's own tenant database, so they arrive
// after the table has rendered, and any of them can be missing. A plan is never
// reported as using fewer seats than it does: a customer whose database could
// not be read is called out rather than silently counted as zero.
const SeatsUsedCell = ({ usage, loading }) => {
  if (loading) return <Spin size="small" />;
  if (!usage || !usage.customer_count) return <Typography.Text type="secondary">—</Typography.Text>;
  if (usage.seats_used === null) return <Typography.Text type="secondary">Unavailable</Typography.Text>;

  return (
    <Space size={4} wrap>
      <span>
        {usage.seats_used}
        {usage.capacity === null ? "" : ` / ${usage.capacity}`}
      </span>
      {usage.customers_over_limit > 0 && (
        <Tooltip title="These customers cannot create new users until the allowance is raised or they move to a larger plan.">
          <Tag color="red">{usage.customers_over_limit} over limit</Tag>
        </Tooltip>
      )}
      {usage.unavailable_customers > 0 && (
        <Tooltip title="Their tenant database is not configured yet or could not be reached, so their seats are not included.">
          <Tag color="default">{usage.unavailable_customers} not counted</Tag>
        </Tooltip>
      )}
    </Space>
  );
};

const PlanManagement = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [plans, setPlans] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });
  // Keyed by plan id. Loaded after the plans themselves, because it reaches
  // into every customer's tenant database and the table should not wait on it.
  const [seatUsage, setSeatUsage] = useState({});
  const [seatUsageLoading, setSeatUsageLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const planFields = [
    { name: "name", label: "Plan Name", required: true, width: 12 },
    { name: "price", label: "Price", type: "number", required: true, width: 12 },
    {
      name: "max_users",
      label: "Maximum Users",
      type: "number",
      // The seat allowance every customer on this plan gets. It belongs here
      // rather than on a subscription because it is part of what the plan is,
      // like price: a customer who needs a different allowance goes on a
      // different plan. Editing it changes the limit for every customer
      // subscribed to this plan.
      required: true,
      integer: true,
      min: 1,
      width: 12,
      placeholder: "e.g. 50",
    },
    {
      name: "billing_cycle",
      label: "Billing Cycle",
      type: "select",
      required: true,
      width: 12,
      options: [
        { value: "Monthly", label: "Monthly" },
        { value: "Yearly", label: "Yearly" },
      ],
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      rows: 4,
      width: 24,
    },
  ];

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: debouncedSearchTerm,
        sortBy: sortConfig.key,
        sortOrder: sortConfig.direction,
      };
      const response = await API.get("/plans", { params });
      setPlans(response.data.data || []);
      setTotalCount(
        response.data.meta && typeof response.data.meta.total === "number"
          ? response.data.meta.total
          : Array.isArray(response.data.data)
            ? response.data.data.length
            : 0
      );
    } catch (error) {
      enqueueSnackbar("Error fetching plans", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchTerm, sortConfig.key, sortConfig.direction, enqueueSnackbar]);

  // Fired off the rendered page of plans rather than with the list request, so
  // the table is usable immediately and a slow or unreachable tenant database
  // delays only these two columns. A failure leaves them blank rather than
  // breaking the page.
  const loadSeatUsage = useCallback(async (planIds) => {
    if (!planIds.length) {
      setSeatUsage({});
      return;
    }

    setSeatUsageLoading(true);
    try {
      const response = await API.get("/plans/seat-usage", { params: { ids: planIds.join(",") } });
      setSeatUsage(
        (response.data?.data || []).reduce((byPlan, usage) => {
          byPlan[usage.plan_id] = usage;
          return byPlan;
        }, {})
      );
    } catch (error) {
      setSeatUsage({});
    } finally {
      setSeatUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    loadSeatUsage(plans.map((plan) => plan.id));
  }, [plans, loadSeatUsage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, sortConfig.key, sortConfig.direction]);

  const handleStatusToggle = async (plan) => {
    const updatedStatus = !plan.is_active;
    try {
      await API.put(`/plans/${plan.id}`, { is_active: updatedStatus });
      enqueueSnackbar(`Plan ${updatedStatus ? "activated" : "deactivated"} successfully`, {
        variant: "success",
      });
      fetchPlans();
    } catch (error) {
      enqueueSnackbar("Error updating plan status", { variant: "error" });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!planToDelete) return;

    try {
      await API.delete(`/plans/${planToDelete.id}`);
      enqueueSnackbar("Plan deleted successfully", { variant: "success" });
      fetchPlans();
    } catch (error) {
      enqueueSnackbar("Error deleting plan", { variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
    }
  };

  const handleOpenCreate = () => {
    setEditingPlan(null);
    setOpen(true);
  };

  const handleFormSubmit = async (formData) => {
    setLoading(true);
    try {
      const { id, createdAt, updatedAt, ...payload } = formData;
      if (editingPlan) {
        await API.put(`/plans/${editingPlan.id}`, payload);
        enqueueSnackbar("Plan updated successfully", { variant: "success" });
      } else {
        await API.post("/plans", { ...payload, is_active: false });
        enqueueSnackbar("Plan created successfully", { variant: "success" });
      }
      fetchPlans();
      setOpen(false);
      setEditingPlan(null);
    } catch (error) {
      // The backend rejects an unusable seat allowance by name, so its message
      // is more use than a generic one.
      const validationErrors = error.response?.data?.errors;
      enqueueSnackbar(
        (Array.isArray(validationErrors) && validationErrors.join(" ")) ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          error.message ||
          "Error saving plan",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", sorter: true, sortOrder: sortConfig.key === "name" ? `${sortConfig.direction}end` : null },
    { title: "Price", dataIndex: "price", key: "price", sorter: true, sortOrder: sortConfig.key === "price" ? `${sortConfig.direction}end` : null, render: (value) => `₹${value}` },
    { title: "Billing Cycle", dataIndex: "billing_cycle", key: "billing_cycle", sorter: true, sortOrder: sortConfig.key === "billing_cycle" ? `${sortConfig.direction}end` : null },
    {
      title: "Maximum Users",
      dataIndex: "max_users",
      key: "max_users",
      align: "right",
      render: (value) =>
        value === null || value === undefined ? (
          // Only plans that predate the field land here. The tenant backend
          // leaves those unenforced, so the column says so rather than showing
          // a blank cell that reads as zero seats.
          <Tag color="default">Not set</Tag>
        ) : (
          value
        ),
    },
    {
      title: "Customers",
      key: "customer_count",
      align: "right",
      // Straight from the master database, unlike the seats beside it, so this
      // is the one figure here that is always available.
      render: (_, plan) => seatUsage[plan.id]?.customer_count ?? <Spin size="small" />,
    },
    {
      title: "Seats Used",
      key: "seats_used",
      align: "right",
      render: (_, plan) => (
        <SeatsUsedCell usage={seatUsage[plan.id]} loading={seatUsageLoading && !seatUsage[plan.id]} />
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (_, plan) => (
        <Space>
          <Switch checked={plan.is_active} onChange={() => handleStatusToggle(plan)} disabled={loading} />
          <Tag color={plan.is_active ? "green" : "default"}>{plan.is_active ? "Active" : "Inactive"}</Tag>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right",
      render: (_, plan) => (
        <RowActions
          disabled={loading}
          items={[
            {
              key: "edit",
              label: "Edit",
              icon: <EditOutlined />,
              onClick: () => {
                setEditingPlan(plan);
                setOpen(true);
              },
            },
            {
              key: "delete",
              label: "Delete",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => {
                setPlanToDelete(plan);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
      ),
    },
  ];

  const emptyState = (
    <div style={{ padding: 48, textAlign: "center" }}>
      <Typography.Title level={4}>No plans found</Typography.Title>
      <Typography.Text type="secondary">
        {searchTerm ? "Clear the search to view all plans." : "Create the first plan to begin."}
      </Typography.Text>
      <div style={{ marginTop: 20 }}>
        {searchTerm ? (
          <Button icon={<ClearOutlined />} onClick={() => setSearchTerm("")}>
            Clear search
          </Button>
        ) : (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Add plan
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Card className="app-page-card" variant="borderless" styles={{ body: { padding: 24 } }}>
      <div className="page-toolbar">
        <div>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            Plan Management
          </Typography.Title>
        </div>
        <Space>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchPlans} loading={loading} />
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreate}
            disabled={loading}
          >
            Add Plan
          </Button>
        </Space>
      </div>

      <Space style={{ width: "100%", marginBottom: 20 }} wrap>
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search plans..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          style={{ width: 300 }}
          disabled={loading}
        />
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={plans}
        loading={loading}
        locale={{ emptyText: emptyState }}
        pagination={{
          current: page + 1,
          pageSize: rowsPerPage,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 25],
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage - 1);
            setRowsPerPage(nextPageSize);
          },
        }}
        onChange={(_, __, sorter) => {
          if (sorter?.field) {
            setSortConfig({
              key: sorter.field,
              direction: sorter.order === "descend" ? "desc" : "asc",
            });
          }
        }}
      />

      <Modal
        open={open}
        destroyOnHidden
        title={
          <div
            style={{
              width: "100%",
              textAlign: "center",
              fontSize: "18px",
              fontWeight: 600,
            }}
          >
            {editingPlan ? "Edit Plan" : "Add New Plan"}
          </div>
        }
        onCancel={() => {
          setOpen(false);
          setEditingPlan(null);
        }}
        footer={null}
        width={720}
      >
        <CustomForm
          fields={planFields}
          initialValues={editingPlan || {}}
          formResetKey={editingPlan?.id || "create"}
          onSubmit={handleFormSubmit}
          loading={loading}
          buttonText={editingPlan ? "Update Plan" : "Create Plan"}
          onCancel={() => {
            setOpen(false);
            setEditingPlan(null);
          }}
        />
      </Modal>

      <Modal
        open={deleteDialogOpen}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setPlanToDelete(null);
        }}
        onOk={handleDeleteConfirm}
        okText="Delete"
        okButtonProps={{ danger: true, loading }}
        title="Confirm delete"
      >
        {/* <Typography.Text>
          Are you sure you want to delete <strong>{planToDelete?.name}</strong>?
        </Typography.Text> */}
        <Space orientation="vertical" size={8} style={{ width: "100%" }}>
          <Typography.Text>
            Are you sure you want to delete this plan?
          </Typography.Text>
          <Typography.Text> Plan: {planToDelete?.name}</Typography.Text>
        </Space>
      </Modal>
    </Card>
  );
};

export default PlanManagement;
